import { clamp } from 'lodash';

import { US_LETTER_SCALE_WIDTH_PX } from '@/modules/presentation/PresentationSlide/PresentationSlide.constants';
import { PresentationCoverSlideConfig } from '@/modules/slideEditor/coverSlide/CoverSlide.types';
import { TITLE_TEXT_SIZE_CONFIG } from '@/modules/slideEditor/shared/titleTextSizeConfig';
import { COLORS } from '@/styles/tokens/colors';

import {
  CoverSlideConfigBodyTextAlignment,
  CoverSlideConfigTitleTextSize,
  PT_PER_REM,
  SLIDE_TEXT_HORIZONTAL_INSET_PX,
  SlideConfigFormShape,
  TEXT_WIDTH_PX_MAX,
  TEXT_WIDTH_PX_MIN,
} from './SlideConfigForm.types';

/**
 * Real defaults for the slider-driven form fields — mirrors the
 * pattern used by the color inputs (`backgroundColor: '#FFFFFF'`,
 * etc.): the form always starts with a sensible canonical value so
 * the input shows it visibly, rather than relying on a placeholder.
 * If the user clears the input, the field becomes '' and the mutation
 * input clears the BE field; on reload, we restore the default.
 */
export const slideConfigDefaultValues: SlideConfigFormShape = {
  backgroundColor: '#FFFFFF',
  useDarkLogo: false,
  footerColor: COLORS.NEUTRAL_GRAY[700],
  backgroundGradientEndColor: '',
  backgroundGradientAngle: '90',
  backgroundOverlayPath: '',
  backgroundOverlayAlpha: '20',
  backgroundOverlayPercentScale: '100',
  backgroundOverlayOriginX: '50',
  backgroundOverlayOriginY: '50',
  textColor: '#000000',
  bodyTextColor: '#000000',
  bodyTextAlignment: CoverSlideConfigBodyTextAlignment.LeftAligned,
  backgroundImageUrl: '',
  backgroundAlpha: '100',
  logoPercentScale: '100',
  useDefaultLogoPosition: true,
  logoOriginX: null,
  logoOriginY: null,
  showLogo: true,
  titleTextSize: CoverSlideConfigTitleTextSize.Medium,
  // 27pt ≈ 2.25rem matches the legacy `Medium` bucket — the same
  // size unconfigured titles render at via the titleTextSize fallback.
  titleSizePt: '27',
  includeFooter: true,
  inheritCover: true,
  backgroundImageOriginX: null,
  backgroundImageOriginY: null,
  textOriginX: null,
  textOriginY: null,
  textWidth: '',
  backgroundImagePercentScale: '100',
};

/**
 * Sample body text for the cover and divider slide previews. It is long
 * enough to wrap across several lines, so admins can see how the body
 * text reflows when they change the text box width.
 */
export const SLIDE_PREVIEW_BODY_TEXT =
  'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.';

/**
 * Inclusive bounds for the visible background-image scale input.
 * Mirrors the form-level validation in SlideConfigFormFields.
 */
export const BACKGROUND_IMAGE_SCALE_MIN = 50;
export const BACKGROUND_IMAGE_SCALE_MAX = 150;

/**
 * Returns the background alpha as a 0-100 number, or null if unset.
 * The form stores the value as a string, so we parse via Number().
 */
function getBackgroundAlpha(formValues: SlideConfigFormShape): number | null {
  const { backgroundAlpha } = formValues;
  if (backgroundAlpha === '') return null;

  return Number(backgroundAlpha);
}

/**
 * Returns the background image scale as a 50-150 number, or null when
 * unset. The form stores the value as a string (mirrors backgroundAlpha)
 * so the empty case is distinguishable from a valid 100.
 */
function getBackgroundImagePercentScale(
  formValues: SlideConfigFormShape
): number | null {
  const { backgroundImagePercentScale } = formValues;
  if (backgroundImagePercentScale === '') return null;

  return Number(backgroundImagePercentScale);
}

/**
 * Returns the logo scale as a 1-200 number, or null when unset. Empty
 * means "use placeholder/default 100%".
 */
function getLogoPercentScale(formValues: SlideConfigFormShape): number | null {
  const { logoPercentScale } = formValues;
  if (logoPercentScale === '') return null;
  return Number(logoPercentScale);
}

/**
 * Returns the title size as a rem number for the BE / render layer, or
 * null when unset. Empty means "fall back to titleTextSize at render
 * time". The form stores pt; we convert to rem here.
 */
function getTitleSizeRem(formValues: SlideConfigFormShape): number | null {
  const { titleSizePt } = formValues;
  if (titleSizePt === '') return null;
  return Number(titleSizePt) / PT_PER_REM;
}

/**
 * Maps a legacy `titleTextSize` bucket (Small/Medium/Large) to its pt
 * equivalent, derived from the bucket's `fontSize` in
 * TITLE_TEXT_SIZE_CONFIG (e.g. Large → '3rem' → 36pt). Used when a
 * pre-migration tenant has only `titleTextSize` saved — we seed the
 * slider with the matching pt value so the form reflects what the
 * renderer currently shows, instead of silently snapping every
 * non-Medium tenant to 27pt on first open.
 */
export function titleSizePtFromBucket(
  size: CoverSlideConfigTitleTextSize
): string {
  // The bucket config stores fontSize as a `${rem}rem` string;
  // parseFloat reads the leading numeric portion.
  const fontSizeRem = parseFloat(TITLE_TEXT_SIZE_CONFIG[size].fontSize);
  return (fontSizeRem * PT_PER_REM).toFixed(1);
}

/**
 * Converts form values to a PresentationCoverSlideConfig for use as a
 * prop override in useMergedSlideDesign.
 */
export function formToSlideDesign(
  formValues: SlideConfigFormShape
): PresentationCoverSlideConfig {
  return {
    useDarkLogo: formValues.useDarkLogo,
    footerColor: formValues.footerColor || null,
    backgroundGradientEndColor: formValues.backgroundGradientEndColor || null,
    backgroundGradientAngle: Number(formValues.backgroundGradientAngle || 90),
    backgroundOverlayPath: formValues.backgroundOverlayPath || null,
    backgroundOverlayAlpha:
      Number(formValues.backgroundOverlayAlpha || 20) / 100,
    backgroundOverlayPercentScale: Number(
      formValues.backgroundOverlayPercentScale || 100
    ),
    backgroundOverlayOriginX: Number(formValues.backgroundOverlayOriginX || 50),
    backgroundOverlayOriginY: Number(formValues.backgroundOverlayOriginY || 50),
    backgroundColor: formValues.backgroundColor || null,
    textColor: formValues.textColor || null,
    bodyTextColor: formValues.bodyTextColor || null,
    bodyTextAlignment: formValues.bodyTextAlignment,
    backgroundImagePath: formValues.backgroundImageUrl || null,
    backgroundImageAlpha: getBackgroundAlpha(formValues),
    titleTextSize: formValues.titleTextSize,
    titleSizeRem: getTitleSizeRem(formValues),
    includeFooter: formValues.includeFooter,
    dividerInheritCover: formValues.inheritCover,
    backgroundImageOriginX: formValues.backgroundImageOriginX,
    backgroundImageOriginY: formValues.backgroundImageOriginY,
    textOriginX: formValues.textOriginX,
    textOriginY: formValues.textOriginY,
    textWidth:
      formValues.textWidth === '' ? null : Number(formValues.textWidth),
    backgroundImagePercentScale: getBackgroundImagePercentScale(formValues),
    logoPercentScale: getLogoPercentScale(formValues),
    useDefaultLogoPosition: formValues.useDefaultLogoPosition,
    logoOriginX: formValues.logoOriginX,
    logoOriginY: formValues.logoOriginY,
    hideLogo: !formValues.showLogo,
  };
}

/**
 * Smallest gap between a dragged slide element and each edge of the
 * slide content area, in slide-coordinate pixels: one 8px spacing unit.
 */
export const SLIDE_DRAG_EDGE_MARGIN_PX = 8;

/**
 * Limit a dragged text X origin so that the text block stays at least
 * `SLIDE_DRAG_EDGE_MARGIN_PX` from each side of the slide and is never
 * narrower than the minimum width. The limit stops the origin; it does
 * not make the block narrower. Apply this only to new drags in the admin
 * preview. Saved values render as stored; admins use "Reset text
 * position" to correct them.
 *
 * `textWidth` is the explicit block width in slide pixels, or null when
 * the block uses its default width.
 */
export function clampTextOriginX(
  textOriginX: number,
  bodyTextAlignment: CoverSlideConfigBodyTextAlignment | null,
  textWidth: number | null
): number {
  // Largest origin that keeps the default-anchored edge inside the margin.
  const insetToMargin =
    SLIDE_TEXT_HORIZONTAL_INSET_PX - SLIDE_DRAG_EDGE_MARGIN_PX;
  // Distance from the default-anchored edge to the opposite margin.
  const insetToFarMargin =
    US_LETTER_SCALE_WIDTH_PX -
    SLIDE_TEXT_HORIZONTAL_INSET_PX -
    SLIDE_DRAG_EDGE_MARGIN_PX;

  // Right-aligned text anchors on its right edge, at
  // (slide width - inset + origin), and extends left by its width.
  if (bodyTextAlignment === CoverSlideConfigBodyTextAlignment.RightAligned) {
    return clamp(
      textOriginX,
      (textWidth ?? TEXT_WIDTH_PX_MAX) - insetToFarMargin,
      insetToMargin
    );
  }

  // Other alignments anchor on the left edge, at (inset + origin).
  // An explicit width moves with the left edge and must fit entirely.
  // An unset width keeps its right edge at the right inset, so the
  // origin must stop before the block is narrower than the minimum.
  const maxOriginX =
    textWidth == null
      ? TEXT_WIDTH_PX_MAX - TEXT_WIDTH_PX_MIN
      : insetToFarMargin - textWidth;
  return clamp(textOriginX, -insetToMargin, maxOriginX);
}

/** Edges of an element on screen, in viewport pixels. */
export interface ScreenEdges {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

/** Inclusive origin limits for one drag, in slide-coordinate pixels. */
export interface DragOriginBounds {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
}

interface GetDragOriginBoundsArgs {
  /** Origin of the element when the drag starts. */
  originX: number;
  originY: number;
  /** Rendered edges of the dragged element. */
  element: ScreenEdges;
  /**
   * Screen Y of the lowest part of the element that must stay visible.
   * For text this is the bottom of the first title line; for a logo it
   * is the bottom of the logo.
   */
  visibleBottom: number;
  /** Rendered edges of the slide content area (above the footer). */
  area: ScreenEdges;
  /** Screen pixels per slide-coordinate pixel. */
  scale: number;
}

/**
 * Convert the rendered layout at drag start into origin limits that keep
 * the element at least `SLIDE_DRAG_EDGE_MARGIN_PX` inside the content
 * area. The content area ends above the footer, so the footer is taken
 * into account. When the element cannot fit on an axis, the top or left
 * limit wins.
 */
export function getDragOriginBounds({
  originX,
  originY,
  element,
  visibleBottom,
  area,
  scale,
}: GetDragOriginBoundsArgs): DragOriginBounds {
  const toSlidePx = (screenPx: number) => screenPx / scale;
  const minX = Math.round(
    originX + toSlidePx(area.left - element.left) + SLIDE_DRAG_EDGE_MARGIN_PX
  );
  const maxX = Math.round(
    originX + toSlidePx(area.right - element.right) - SLIDE_DRAG_EDGE_MARGIN_PX
  );
  const minY = Math.round(
    originY + toSlidePx(area.top - element.top) + SLIDE_DRAG_EDGE_MARGIN_PX
  );
  const maxY = Math.round(
    originY + toSlidePx(area.bottom - visibleBottom) - SLIDE_DRAG_EDGE_MARGIN_PX
  );
  return {
    minX,
    maxX: Math.max(minX, maxX),
    minY,
    maxY: Math.max(minY, maxY),
  };
}
